import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../axiosInstance/axiosInstance";
import { ApiErrorResponse } from "../../../api/types/ApiErrorResponse";
import { toApiError } from "../../../api/utils/errorHandling";
import { SessionOccurrencesResponse } from "../contracts/SessionOccurrencesResponse";
import { CancelOccurrenceRequest } from "../contracts/CancelOccurrenceRequest";
import { RescheduleOccurrenceRequest } from "../contracts/RescheduleOccurrenceRequest";
import { RetimeOccurrenceRequest } from "../contracts/RetimeOccurrenceRequest";
import { RestoreOccurrenceRequest } from "../contracts/RestoreOccurrenceRequest";
import { SessionResponse } from "../contracts/SessionResponse";
import { SessionOccurrence } from "../models/SessionOccurrence";
import { Session } from "../models/Session";
import { OccurrenceResponseMapper } from "../mappers/OccurrenceResponseMapper";
import { SessionResponseMapper } from "../mappers/SessionResponseMapper";

const occurrenceMapper = new OccurrenceResponseMapper();
const sessionMapper = new SessionResponseMapper();

/** Load the computed occurrences (including cancelled ones) of one session in [from, to]. */
export const fetchSessionOccurrences = createAsyncThunk<
  { bookingId: string; occurrences: SessionOccurrence[] },
  { bookingId: string; from: string; to: string },
  { rejectValue: ApiErrorResponse }
>("occurrences/fetch", async ({ bookingId, from, to }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<SessionOccurrencesResponse>(
      `/bookings/${bookingId}/occurrences`,
      { params: { from, to } }
    );
    return {
      bookingId,
      occurrences: occurrenceMapper.toOccurrences(response.data.occurrences),
    };
  } catch (error: unknown) {
    return rejectWithValue(toApiError(error, "Failed to load occurrences"));
  }
});

/** Cancel a single occurrence; the rest of the series is untouched. */
export const cancelOccurrence = createAsyncThunk<
  Session,
  { bookingId: string; request: CancelOccurrenceRequest },
  { rejectValue: ApiErrorResponse }
>("occurrences/cancel", async ({ bookingId, request }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<SessionResponse>(
      `/bookings/${bookingId}/occurrences/cancel`,
      request
    );
    return sessionMapper.toSession(response.data);
  } catch (error: unknown) {
    return rejectWithValue(toApiError(error, "Failed to cancel occurrence"));
  }
});

/** Move a single occurrence to a new date and time. */
export const rescheduleOccurrence = createAsyncThunk<
  Session,
  { bookingId: string; request: RescheduleOccurrenceRequest },
  { rejectValue: ApiErrorResponse }
>("occurrences/reschedule", async ({ bookingId, request }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<SessionResponse>(
      `/bookings/${bookingId}/occurrences/reschedule`,
      request
    );
    return sessionMapper.toSession(response.data);
  } catch (error: unknown) {
    return rejectWithValue(toApiError(error, "Failed to reschedule occurrence"));
  }
});

/** Change a single occurrence's start time on the same date. */
export const retimeOccurrence = createAsyncThunk<
  Session,
  { bookingId: string; request: RetimeOccurrenceRequest },
  { rejectValue: ApiErrorResponse }
>("occurrences/retime", async ({ bookingId, request }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<SessionResponse>(
      `/bookings/${bookingId}/occurrences/retime`,
      request
    );
    return sessionMapper.toSession(response.data);
  } catch (error: unknown) {
    return rejectWithValue(toApiError(error, "Failed to change occurrence time"));
  }
});

/** Remove a single occurrence's override, returning it to the series schedule. */
export const restoreOccurrence = createAsyncThunk<
  Session,
  { bookingId: string; request: RestoreOccurrenceRequest },
  { rejectValue: ApiErrorResponse }
>("occurrences/restore", async ({ bookingId, request }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<SessionResponse>(
      `/bookings/${bookingId}/occurrences/restore`,
      request
    );
    return sessionMapper.toSession(response.data);
  } catch (error: unknown) {
    return rejectWithValue(toApiError(error, "Failed to restore occurrence"));
  }
});
